import { initializeApp } from "firebase/app";
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut as firebaseSignOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  connectAuthEmulator
} from "firebase/auth";
import { getFirestore, enableIndexedDbPersistence, connectFirestoreEmulator } from "firebase/firestore";
import { getAnalytics } from "firebase/analytics";
import { connectStorageEmulator, getStorage } from "firebase/storage";
import { getMessaging, getToken, onMessage, isSupported, Messaging } from "firebase/messaging";
import { config } from "@/config/environment";
import { measureAsyncOperation, LoadingPhase } from "@/hooks/useLoadingMetrics";

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyA4f7SnMnl1qwEfhW6xgHL4MTX_6dI2Pws",
  authDomain: "k3-whatsapp-monitor.firebaseapp.com",
  projectId: "k3-whatsapp-monitor",
  storageBucket: "k3-whatsapp-monitor.firebasestorage.app",
  messagingSenderId: "192887313253",
  appId: "1:192887313253:web:b75ca04695f09b0fca878c",
  measurementId: "G-ZTREEYKED8"
};

// Get the VAPID key from environment configuration
const vapidKey = config.firebase.fcm.vapidKey;

// Get the reCAPTCHA site key from environment configuration
const recaptchaSiteKey = config.firebase.recaptcha.siteKey;

// Initialize Firebase
export const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

const storage = getStorage(app);

// Connect to local emulators only when explicitly enabled
// Set VITE_USE_FIREBASE_EMULATORS=true in .env to use emulators
const useEmulators = import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true';
if (useEmulators) {
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, 'localhost', 8080);
  connectStorageEmulator(storage, 'localhost', 9199);
}

// IndexedDB persistence setup - lazy initialization
let persistenceInitialized = false;
let persistenceInitPromise: Promise<void> | null = null;

const initPersistence = async (): Promise<void> => {
  if (persistenceInitialized || persistenceInitPromise) {
    return persistenceInitPromise || Promise.resolve();
  }

  if (useEmulators) {
    persistenceInitialized = true;
    return Promise.resolve();
  }

  persistenceInitPromise = (async () => {
    await measureAsyncOperation(
      LoadingPhase.FIREBASE_INITIALIZATION,
      async () => {
        try {
          await enableIndexedDbPersistence(db);
          console.log('IndexedDB persistence enabled successfully');
          return { success: true, feature: 'indexeddb-persistence' };
        } catch (err: unknown) {
           const error = err as { code?: string };
           if (error.code === 'failed-precondition') {
             console.warn('Persistence failed: Multiple tabs open');
           } else if (error.code === 'unimplemented') {
             console.warn('Persistence not available in this browser');
           } else {
             console.warn('Error enabling persistence:', err);
           }
           return { success: false, feature: 'indexeddb-persistence', error: error.code };
        }
      },
      {
        environment: process.env.NODE_ENV,
        feature: 'indexeddb-persistence'
      }
    );
    persistenceInitialized = true;
  })();

  return persistenceInitPromise;
};

const googleProvider = new GoogleAuthProvider();

// Messaging setup - load conditionally
let messaging: Messaging | null = null;
let messagingInitialized = false;
let messagingInitPromise: Promise<Messaging | null> | null = null;

// Lazy initialization of messaging - only when needed
const initMessaging = async () => {
  if (messagingInitialized) {
    return messaging;
  }
  
  if (messagingInitPromise) {
    return messagingInitPromise;
  }
  
  messagingInitPromise = (async () => {
    if (typeof window !== 'undefined') {
      try {
        const isMessagingSupported = await isSupported();
        
        if (isMessagingSupported) {
          console.log("Firebase messaging is supported in this browser");
          messaging = getMessaging(app);
          
          // Track the successful initialization
          await measureAsyncOperation(
            LoadingPhase.FIREBASE_INITIALIZATION,
            async () => {
              return {
                messaging: 'initialized',
                supported: true,
                userAgent: navigator.userAgent,
                serviceWorkerSupported: 'serviceWorker' in navigator
              };
            },
            {
              feature: 'firebase-messaging',
              userAgent: navigator.userAgent,
              serviceWorkerSupported: 'serviceWorker' in navigator
            }
          );
          
          messagingInitialized = true;
          return messaging;
        } else {
          console.log("Firebase messaging is not supported in this browser");
          console.log("User agent:", navigator.userAgent);
          console.log("Service worker supported:", 'serviceWorker' in navigator);
          
          // Track the unsupported case
          await measureAsyncOperation(
            LoadingPhase.FIREBASE_INITIALIZATION,
            async () => {
              return {
                messaging: null,
                supported: false,
                userAgent: navigator.userAgent,
                serviceWorkerSupported: 'serviceWorker' in navigator
              };
            },
            {
              feature: 'firebase-messaging',
              userAgent: navigator.userAgent,
              serviceWorkerSupported: 'serviceWorker' in navigator
            }
          );
        }
      } catch (error) {
        console.error("Error checking messaging support:", error);
        console.log("Detailed error info:", {
          message: error instanceof Error ? error.message : String(error),
          name: error instanceof Error ? error.name : 'Unknown',
          stack: error instanceof Error ? error.stack : 'No stack trace'
        });
        
        // Track the error case
        await measureAsyncOperation(
          LoadingPhase.FIREBASE_INITIALIZATION,
          async () => {
            return {
              messaging: null,
              supported: false,
              error: error instanceof Error ? error.message : String(error),
              userAgent: navigator.userAgent
            };
          },
          {
            feature: 'firebase-messaging',
            userAgent: navigator.userAgent,
            error: error instanceof Error ? error.message : String(error)
          }
        );
      } finally {
        messagingInitialized = true;
      }
    }
    messagingInitialized = true;
    return null;
  })();
  
  return messagingInitPromise;
};

// Export initMessaging for manual initialization when needed
const getMessagingInstance = async () => {
  if (!messaging && !messagingInitialized) {
    await initMessaging();
  }
  return messaging;
};

// Simplified FCM token request function
const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.log("This browser does not support notifications");
    return null;
  }

  try {
    // First, request notification permission
    console.log("Requesting notification permission...");
    const permission = await Notification.requestPermission();
    console.log("Permission result:", permission);
    
    if (permission !== 'granted') {
      console.log("Notification permission denied");
      return null;
    }
    
    // Initialize messaging if not already done
    const messagingInstance = await getMessagingInstance();
    
    if (!messagingInstance) {
      console.log("Messaging not supported");
      return null;
    }

    // Register the service worker first
    let swRegistration;
    try {
      console.log("Registering service worker...");
      swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });
      console.log('Service worker registered successfully:', swRegistration);
    } catch (error) {
      console.error('Service worker registration failed:', error);
      return null;
    }

    // Wait for the service worker to be activated
    if (swRegistration.installing) {
      console.log("Service worker is installing, waiting for it to activate...");
      await new Promise<void>((resolve) => {
        swRegistration.installing!.addEventListener('statechange', (e) => {
          if ((e.target as ServiceWorker).state === 'activated') {
            console.log("Service worker now activated");
            resolve();
          }
        });
      });
    }

    // Get the FCM token
    console.log("Getting FCM token with VAPID key...");
    
    // Check if browser is Safari
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    try {
      let currentToken;
      
      if (isSafari) {
        console.log("Safari detected, using alternative method");
        // Safari requires different handling
        currentToken = await getToken(messagingInstance, {
          vapidKey: vapidKey,
          serviceWorkerRegistration: swRegistration
        });
      } else {
        // Standard approach for other browsers
        currentToken = await getToken(messagingInstance, {
          vapidKey: vapidKey,
          serviceWorkerRegistration: swRegistration
        });
      }
      
      if (currentToken) {
        console.log("FCM token retrieved successfully:", currentToken);
        return currentToken;
      } else {
        console.log('No FCM token available.');
        return null;
      }
    } catch (tokenError) {
      console.error('Error getting FCM token:', tokenError);
      console.log("Detailed token error:", {
        message: tokenError instanceof Error ? tokenError.message : String(tokenError),
        name: tokenError instanceof Error ? tokenError.name : 'Unknown',
        code: tokenError instanceof Error && 'code' in tokenError ? (tokenError as Error & { code?: string }).code : 'No error code'
      });
      return null;
    }
  } catch (error) {
    console.error('Error in requestNotificationPermission:', error);
    console.log("Detailed error info:", {
      message: error instanceof Error ? error.message : String(error),
      name: error instanceof Error ? error.name : 'Unknown',
      stack: error instanceof Error ? error.stack : 'No stack trace'
    });
    return null;
  }
};

// Keep the getFCMToken function for backward compatibility but make it use the new approach
const getFCMToken = async () => {
  return await requestNotificationPermission();
};

// Setup message handler
const setupMessageListener = async () => {
  const messagingInstance = await getMessagingInstance();
  if (!messagingInstance) return;
  
  onMessage(messagingInstance, (payload) => {
    console.log('Message received:', payload);
    // Handle foreground notifications
    if (payload.notification) {
      const { title, body } = payload.notification;
      // Create a notification if the app is in the foreground
      if (Notification.permission === 'granted') {
        new Notification(title as string, {
          body: body as string,
          icon: '/favicon.png'
        });
      }
    }
  });
};

// Initialize Analytics (will be lazy-loaded by the analytics module)
const initAnalytics = () => {
  if (typeof window !== 'undefined') {
    return getAnalytics(app);
  }
  return null;
};

// Auth functions
const signInWithEmail = (email: string, password: string) => {
  return signInWithEmailAndPassword(auth, email, password);
};

const signUpWithEmail = (email: string, password: string) => {
  return createUserWithEmailAndPassword(auth, email, password);
};

const signInWithGoogle = () => {
  return signInWithPopup(auth, googleProvider);
};

const signOut = () => {
  return firebaseSignOut(auth);
};

// For WhatsApp JIDs, we pass through the ID unchanged
export const sanitizeFirestoreId = (id: string): string => {
  // WhatsApp JIDs and other IDs can be used directly
  // Firestore allows most characters in document IDs except for:
  // - Forward slashes (/)
  // - Double dots (..)
  // - Double underscores (__)
  // - Double dots within a path segment (./..)
  // - ID cannot be just "." or ".."
  
  return id;
};

/**
 * Firestore Rules Recommendations:
 * 
 * To allow timestamp updates for conversations, add this specific rule to your Firestore rules:
 * 
 * ```
 * // Specific rule for conversation timestamp updates
 * match /connections/{connectionId}/conversations/{conversationId} {
 *   allow update: if request.auth != null &&
 *     request.resource.data.diff(resource.data).affectedKeys().hasOnly(['lastFlaggedReviewAt', 'lastViewedBy']) &&
 *     request.resource.data.lastViewedBy == request.auth.uid &&
 *     (
 *       resource.data.userId == request.auth.uid ||
 *       get(/databases/$(database)/documents/connections/$(connectionId)).data.userId == request.auth.uid
 *     );
 * }
 * ```
 * 
 * This rule:
 * 1. Only allows updates (not creates, deletes, etc.)
 * 2. Ensures the user is authenticated
 * 3. Only allows changes to the specific fields: lastFlaggedReviewAt and lastViewedBy
 * 4. Requires lastViewedBy to be set to the current user's ID
 * 5. Ensures the user has permission to access this conversation
 */

export {
  auth,
  db,
  storage,
  signInWithEmail,
  signUpWithEmail,
  signInWithGoogle,
  signOut,
  onAuthStateChanged,
  initAnalytics,
  requestNotificationPermission,
  getFCMToken,
  setupMessageListener,
  getMessagingInstance,
  initPersistence,
  vapidKey,
  recaptchaSiteKey
};
